import { Event } from '@bugsnag/js';

/**
 * Configuration for filtering Bugsnag errors
 */
export interface BugsnagErrorFilterConfig {
  /**
   * List of file patterns to ignore (e.g., 'analytics.js', 'third-party.js')
   * Errors with stack traces containing these file names will be filtered out
   */
  ignoredFiles?: string[];

  /**
   * List of error message patterns to ignore (e.g., 'ResizeObserver loop')
   * Errors with messages matching these patterns will be filtered out
   */
  ignoredMessages?: string[];

  /**
   * List of error types to ignore (e.g., 'ChunkLoadError')
   * Errors with these error class names will be filtered out
   */
  ignoredErrorTypes?: string[];

  /**
   * Custom filter function for advanced filtering logic
   * Return false to prevent the error from being reported
   */
  customFilter?: (event: Event) => boolean;
}

/**
 * Default configuration for common third-party errors that should be ignored
 */
export const DEFAULT_BUGSNAG_IGNORED_ERRORS: BugsnagErrorFilterConfig = {
  ignoredFiles: [],
  ignoredMessages: [
    'ResizeObserver loop limit exceeded', // Browser ResizeObserver warning
    'ResizeObserver loop completed with undelivered notifications', // Browser ResizeObserver warning
  ],
  ignoredErrorTypes: [],
};

/**
 * Creates a Bugsnag onError callback that filters out unwanted errors
 * based on the provided configuration
 *
 * @param config - Configuration specifying which errors to ignore
 * @returns An onError callback function for Bugsnag configuration
 *
 * @example
 * ```typescript
 * import Bugsnag from '@bugsnag/js';
 * import { createBugsnagErrorFilter, DEFAULT_BUGSNAG_IGNORED_ERRORS } from '@campus/utils';
 *
 * Bugsnag.start({
 *   apiKey: 'your-api-key',
 *   onError: createBugsnagErrorFilter({
 *     ...DEFAULT_BUGSNAG_IGNORED_ERRORS,
 *     ignoredFiles: [...DEFAULT_BUGSNAG_IGNORED_ERRORS.ignoredFiles, 'custom-plugin.js'],
 *     ignoredMessages: [...DEFAULT_BUGSNAG_IGNORED_ERRORS.ignoredMessages, 'Custom error pattern'],
 *   }),
 * });
 * ```
 */
export function createBugsnagErrorFilter(config: BugsnagErrorFilterConfig = {}): (event: Event) => boolean {
  const { ignoredFiles = [], ignoredMessages = [], ignoredErrorTypes = [], customFilter } = config;

  return (event: Event): boolean => {
    // Check if error originates from an ignored file
    if (ignoredFiles.length > 0) {
      const hasIgnoredFile = event.errors[0]?.stacktrace?.some((frame) =>
        ignoredFiles.some((ignoredFile) => frame.file?.includes(ignoredFile))
      );
      if (hasIgnoredFile) {
        return false;
      }
    }

    // Check if error message matches an ignored pattern
    if (ignoredMessages.length > 0) {
      const errorMessage = event.errors[0]?.errorMessage || '';
      const hasIgnoredMessage = ignoredMessages.some((pattern) => errorMessage.includes(pattern));
      if (hasIgnoredMessage) {
        return false;
      }
    }

    // Check if error type matches an ignored type
    if (ignoredErrorTypes.length > 0) {
      const errorClass = event.errors[0]?.errorClass || '';
      const hasIgnoredType = ignoredErrorTypes.includes(errorClass);
      if (hasIgnoredType) {
        return false;
      }
    }

    // Apply custom filter if provided
    if (customFilter) {
      return customFilter(event);
    }

    // Report the error by default
    return true;
  };
}
